import Api from './api';
import {FrameLoader} from './frame/frame';
import type {Config} from './types/types';
import {jigurda} from './utils/jigurda';
import {createStorage, Storage} from './utils/storage';
import {getUtcToday, isDateOlderThan} from './utils/time';

export class FeedbackLoader {
	private readonly config: Config;
	private readonly storage: Storage;
	private frameLoader: FrameLoader | null = null;
	private api: Api;

	constructor(config: Config) {
		this.config = {...config};
		this.storage = createStorage(this.config.userId);
		this.api = new Api(this.config);

		this.onClose = this.onClose.bind(this);
	}

	getSaltedUser() {
		const {userId, surveyId} = this.config;

		return `${userId}${surveyId}${getUtcToday()}`;
	}

	/**
	 * Проверить, можно ли показать CSAT опрос пользователю
	 *
	 * - Проверка происходит раз в сутки (сохраняем последнее время проверки в local storage)
	 * - Сначала проверяем, входит ли сочетание "пользователь-опрос-дата" в джигурду (порог задается в конфиге)
	 * - После этого проверяем, достаточно давно ли голосовал пользователь
	 * - Если да, загружаем iframe с опросом
	 *
	 * При наличии параметра dryRun проверяем только джигурду и отправляем радар при успешной проверке
	 */
	update(): Promise<void> {
		const today = getUtcToday();
		const lastCheck = this.storage.get('lastCheck');

		if (this.config.forced) {
			this.show();
			return Promise.resolve();
		}

		if (today === lastCheck) {
			// Сегодня уже проверяли, больше не нужно
			return Promise.reject();
		}

		this.storage.set('lastCheck', today);

		if (!this.checkLastShow()) {
			// Прошло недостаточно времени с прошлого показа
			return Promise.reject();
		}

		const {userId, permille, dryRun, radar, surveyId} = this.config;

		radar?.('feedback-loader_update', {
			userId,
			permille,
			surveyId,
			dryRun,
		});

		if (!this.checkJigurda()) {
			return Promise.reject();
		}

		radar?.('feedback-loader_jigurda-pass', {
			userId,
			permille,
			surveyId,
			dryRun,
		});

		if (dryRun) {
			// В этом режиме нужно только проверить джигурду, показывать опрос не нужно
			return Promise.resolve();
		}

		// Проверяем в апи, что мы еще не голосовали
		return this.checkApi().then((isOk) => {
			radar?.(`feedback-loader_api-${isOk ? 'pass' : 'fail'}`, {
				userId,
				permille,
				surveyId,
				dryRun,
			});

			if (isOk) {
				// Мы не голосовали, можно показать опрос
				this.show();
			} else {
				throw false;
			}
		});
	}

	checkLastShow(): boolean {
		const lastShow = this.storage.get('lastShow');
		const {repeatInterval} = this.config;

		if (!repeatInterval || !lastShow) {
			return true;
		}

		return isDateOlderThan(lastShow, repeatInterval);
	}

	/**
	 * Проверить, входит ли сочетание "пользователь-опрос-дата" в джигурду (порог задается в конфиге)
	 */
	checkJigurda(): boolean {
		const {permille} = this.config;

		const saltedUser = this.getSaltedUser();
		const crc = jigurda(saltedUser);

		return crc < permille;
	}

	/**
	 * Проверить, достаточно ли давно пользователь голосовал в опросе
	 */
	checkApi(): Promise<boolean> {
		const {surveyId} = this.config;

		return this.api.checkFeedbackVoted(surveyId).then((voted) => {
			return !voted;
		});
	}

	show() {
		if (this.frameLoader) {
			return;
		}

		const {surveyId, userId, domain, styles, darkTheme, radar} = this.config;

		this.frameLoader = new FrameLoader({
			surveyId,
			domain,
			styles,
			darkTheme,
			onClose: this.onClose,
		});

		this.frameLoader.open();

		this.storage.set('lastShow', getUtcToday());

		radar?.('feedback-loader_show', {
			userId,
			surveyId,
		});
	}

	close() {
		if (!this.frameLoader) {
			return;
		}

		this.frameLoader.close();

		const {surveyId, userId, radar} = this.config;

		radar?.('feedback-loader_close', {
			userId,
			surveyId,
		});
	}

	setDarkTheme(value: boolean) {
		this.config.darkTheme = !!value;

		this.frameLoader?.setDarkTheme(value);

		const {surveyId, userId, radar} = this.config;
		radar?.(`feedback-loader_set-dark_${value ? 'on' : 'off'}`, {
			userId,
			surveyId,
		});
	}

	onClose() {
		this.frameLoader = null;
	}

	/**
	 * Для тестов
	 */
	_getStorage() {
		return this.storage;
	}
}
